// CppSectionEditor — right-hand editor panel for one CPP/PCI section: guidance
// intro, discrete fields (from the reference doc's tables), a free-text
// narrative box, a "not applicable" toggle, per-section image uploads, an
// AI-assisted narrative drafting button, and evidence attachments (Phase 2)
// linking straight into the Document Library.
import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X, ImageOff, Sparkles, Paperclip, FileText, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import DocumentLinkDialog from "@/components/DocumentLinkDialog";
import { useOfflinePhotoQueue } from "@/hooks/useOfflinePhotoQueue";
import { PendingPhotoThumbs } from "@/components/PendingPhotoThumbs";

export default function CppSectionEditor({ recordId, section, numberLabel, canEdit, onSaved }) {
    const [draftFields, setDraftFields] = useState({});
    const [draftNarrative, setDraftNarrative] = useState("");
    const [imageUrls, setImageUrls] = useState({});
    const [imgLabel, setImgLabel] = useState("");
    const fileRef = useRef(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiDraft, setAiDraft] = useState(null);
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    const [clientId, setClientId] = useState(null);

    const { pendingPhotos, queuePhoto: queuePhotoOnly, retry: retryQueuedPhoto, discard: discardQueuedPhoto, discardAll: discardAllPendingPhotos } = useOfflinePhotoQueue({
        entityType: "cpp_section", entityId: `${recordId}::${section.code}`, enabled: !!recordId,
        recordLabel: `CPP — ${section.title || section.code}`,
        onUploaded: async () => { try { const { data } = await api.get(`/cpp/${recordId}`); onSaved(data.sections); } catch { /* best-effort */ } },
    });

    useEffect(() => {
        setDraftFields(Object.fromEntries((section.fields || []).map((f) => [f.key, f.value || ""])));
        setDraftNarrative(section.narrative || "");
        setAiDraft(null);
    }, [section.code]);

    useEffect(() => {
        api.get(`/cpp/${recordId}`).then((r) => setClientId(r.data.client_id)).catch(() => {});
    }, [recordId]);

    useEffect(() => {
        let mounted = true;
        const urls = {};
        (async () => {
            for (const img of section.images || []) {
                try {
                    const res = await api.get(`/cpp/${recordId}/images/${img.id}/file`, { responseType: "blob" });
                    urls[img.id] = URL.createObjectURL(res.data);
                } catch { /* skip broken image */ }
            }
            if (mounted) setImageUrls({ ...urls });
        })();
        return () => { mounted = false; Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section.code, (section.images || []).length]);

    const patchSection = async (payload) => {
        try {
            const { data } = await api.patch(`/cpp/${recordId}/sections/${section.code}`, payload);
            onSaved(data.sections);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    };

    const saveFields = () => {
        const payload = (section.fields || []).map((f) => ({
            key: f.key, label: f.label, field_type: f.field_type, value: draftFields[f.key] ?? "",
        }));
        patchSection({ fields: payload });
    };

    const toggleNotApplicable = () => patchSection({ not_applicable: !section.not_applicable });

    const uploadImage = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        let queuedCount = 0;
        for (const file of files) {
            try { await queuePhotoOnly(file, { label: imgLabel || "" }); queuedCount += 1; }
            catch (e) { toast.error(`Could not save ${file.name} on this device`); }
        }
        setImgLabel("");
        if (fileRef.current) fileRef.current.value = "";
        if (!queuedCount) return;
        if (navigator.onLine) toast.success(queuedCount > 1 ? `${queuedCount} images added — uploading…` : "Image added — uploading…");
        else toast.info(queuedCount > 1 ? `${queuedCount} images saved on this device — will upload automatically` : "Image saved on this device — will upload automatically");
    };

    const deleteImage = async (imageId) => {
        try {
            const { data } = await api.delete(`/cpp/${recordId}/sections/${section.code}/images/${imageId}`);
            onSaved(data.sections);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete image"); }
    };

    const runAiDraft = async () => {
        setAiBusy(true);
        try {
            const { data } = await api.post(`/cpp/${recordId}/sections/${section.code}/ai-draft`);
            setAiDraft(data.draft);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI drafting failed"); }
        setAiBusy(false);
    };

    const useAiDraft = () => {
        setDraftNarrative(aiDraft);
        patchSection({ narrative: aiDraft });
        setAiDraft(null);
        toast.success("Draft inserted");
    };

    const uploadEvidence = async (file, label) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", label || "");
        const { data } = await api.post(`/cpp/${recordId}/sections/${section.code}/evidence/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onSaved(data.sections);
        toast.success("Evidence attached");
    };

    const linkEvidence = async (documentId) => {
        const { data } = await api.post(`/cpp/${recordId}/sections/${section.code}/evidence/link`, { document_id: documentId });
        onSaved(data.sections);
        toast.success("Evidence linked");
    };

    const removeEvidence = async (evidenceId) => {
        try {
            const { data } = await api.delete(`/cpp/${recordId}/sections/${section.code}/evidence/${evidenceId}`);
            onSaved(data.sections);
        } catch (e) { toast.error("Could not remove evidence"); }
    };

    const viewEvidence = async (docId) => {
        try {
            const resp = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] }));
            window.open(url, "_blank");
        } catch { toast.error("Could not open file"); }
    };

    return (
        <div className="flex-1 space-y-5" data-testid="cpp-section-editor">
            <div>
                <h3 className="text-xl font-bold text-[#1F1F1F]">{numberLabel ? `${numberLabel}  ` : ""}{section.title}</h3>
                {section.intro && <p className="text-[13px] text-[#6F6A64] mt-1 leading-relaxed">{section.intro}</p>}
            </div>

            {canEdit && (
                <label className="inline-flex items-center gap-2 text-[12.5px] font-medium text-[#4A4640]" data-testid="cpp-not-applicable-toggle">
                    <input type="checkbox" checked={!!section.not_applicable} onChange={toggleNotApplicable} className="rounded" />
                    Not applicable to this project
                </label>
            )}

            {!section.not_applicable && (
                <>
                    {(section.fields || []).length > 0 && (
                        <div className="grid sm:grid-cols-2 gap-3 bg-white border rounded-xl p-4" style={{ borderColor: "#E5E0DA" }}>
                            {section.fields.map((f) => (
                                <div key={f.key} className={f.field_type === "textarea" ? "sm:col-span-2" : ""}>
                                    <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">{f.label}</label>
                                    {f.field_type === "textarea" ? (
                                        <textarea data-testid={`cpp-field-${f.key}`} disabled={!canEdit} rows={2}
                                            className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-sm disabled:bg-[#F6F4F0]"
                                            value={draftFields[f.key] ?? ""} onChange={(e) => setDraftFields((d) => ({ ...d, [f.key]: e.target.value }))}
                                            onBlur={saveFields} />
                                    ) : (
                                        <input data-testid={`cpp-field-${f.key}`} disabled={!canEdit}
                                            type={f.field_type === "date" ? "date" : "text"}
                                            className="w-full mt-1 h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm disabled:bg-[#F6F4F0]"
                                            value={draftFields[f.key] ?? ""} onChange={(e) => setDraftFields((d) => ({ ...d, [f.key]: e.target.value }))}
                                            onBlur={saveFields} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {!section.is_container && (
                        <div>
                            <div className="flex items-center justify-between">
                                <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Narrative</label>
                                {canEdit && (
                                    <button data-testid="cpp-ai-draft-btn" onClick={runAiDraft} disabled={aiBusy}
                                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border"
                                        style={{ borderColor: "#C9A0DB", color: "#7B3FA0" }}>
                                        {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI-draft this section
                                    </button>
                                )}
                            </div>
                            <textarea data-testid="cpp-narrative" disabled={!canEdit} rows={10}
                                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[#E5E0DA] bg-white text-sm leading-relaxed disabled:bg-[#F6F4F0]"
                                placeholder="Describe the arrangements for this section…"
                                value={draftNarrative} onChange={(e) => setDraftNarrative(e.target.value)}
                                onBlur={() => patchSection({ narrative: draftNarrative })} />
                            {aiDraft && (
                                <div data-testid="cpp-ai-draft-preview" className="mt-2 rounded-xl border p-3.5 space-y-2" style={{ borderColor: "#C9A0DB", background: "#FAF5FC" }}>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#7B3FA0" }}>AI draft suggestion</p>
                                    <p className="text-[13px] leading-relaxed whitespace-pre-line text-[#2D2D2D]">{aiDraft}</p>
                                    <div className="flex items-center gap-2">
                                        <button data-testid="cpp-ai-draft-use" onClick={useAiDraft}
                                            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white" style={{ background: "#7B3FA0" }}>Use this draft</button>
                                        <button data-testid="cpp-ai-draft-discard" onClick={() => setAiDraft(null)}
                                            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Discard</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="bg-white border rounded-xl p-4 space-y-3" style={{ borderColor: "#E5E0DA" }}>
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Photos / drawings</label>
                            {canEdit && (
                                <div className="flex items-center gap-2">
                                    <input data-testid="cpp-image-label" placeholder="Caption (optional)" value={imgLabel}
                                        onChange={(e) => setImgLabel(e.target.value)}
                                        className="h-8 px-2.5 rounded-lg border border-[#E5E0DA] text-[12px] w-40" />
                                    <input ref={fileRef} type="file" accept="image/*" multiple data-testid="cpp-image-file-input"
                                        className="hidden" onChange={(e) => uploadImage(e.target.files)} />
                                    <button data-testid="cpp-image-upload-btn" onClick={() => fileRef.current?.click()}
                                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-white text-[12px] font-semibold"
                                        style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                        <Upload size={12} /> Upload
                                    </button>
                                </div>
                            )}
                        </div>
                        {(section.images || []).length === 0 ? (
                            <p className="text-[12px] text-[#9A968F] flex items-center gap-1.5"><ImageOff size={13} /> No images yet.</p>
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {section.images.map((img) => (
                                    <div key={img.id} className="relative group" data-testid={`cpp-image-${img.id}`}>
                                        {imageUrls[img.id] ? (
                                            <img src={imageUrls[img.id]} alt={img.label || img.filename}
                                                className="w-full h-24 object-cover rounded-lg border" style={{ borderColor: "#E5E0DA" }} />
                                        ) : (
                                            <div className="w-full h-24 rounded-lg border bg-[#F6F4F0] flex items-center justify-center" style={{ borderColor: "#E5E0DA" }}>
                                                <Loader2 size={14} className="animate-spin text-[#9A968F]" />
                                            </div>
                                        )}
                                        {img.label && <p className="text-[10.5px] text-[#6F6A64] mt-1 truncate">{img.label}</p>}
                                        {canEdit && (
                                            <button data-testid={`cpp-image-delete-${img.id}`} onClick={() => deleteImage(img.id)}
                                                className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <X size={11} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {pendingPhotos.length > 0 && (
                            <div data-testid="cpp-photo-sync-status" className="flex items-center gap-2 rounded-lg px-2.5 py-2 border" style={{ background: "#FBEFE3", borderColor: "#F0DCC2" }}>
                                <span className="text-[12px] text-[#9F4212] font-semibold flex-1">
                                    {pendingPhotos.length} image{pendingPhotos.length !== 1 ? "s" : ""} {pendingPhotos.every((p) => p.failed) ? "failed to upload — tap below to retry or discard." : "saved on this device — uploading…"}
                                </span>
                                {canEdit && (
                                    <button type="button" data-testid="cpp-discard-all-pending-photos"
                                        onClick={() => { if (window.confirm(`Discard all ${pendingPhotos.length} pending image(s)? They will not be uploaded.`)) discardAllPendingPhotos(); }}
                                        className="text-[11px] font-semibold text-[#9F4212] hover:underline shrink-0">Discard all</button>
                                )}
                            </div>
                        )}
                        <PendingPhotoThumbs pending={pendingPhotos} testId="cpp-pending-photos" onRetry={retryQueuedPhoto} onDiscard={canEdit ? discardQueuedPhoto : null} />
                    </div>

                    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="cpp-evidence-panel" style={{ borderColor: "#E5E0DA" }}>
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide">Evidence attachments</label>
                            {canEdit && (
                                <button data-testid="cpp-evidence-add-btn" onClick={() => setEvidenceOpen(true)}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold border"
                                    style={{ borderColor: "var(--brand-accent,#C75A1B)", color: "var(--brand-accent,#C75A1B)" }}>
                                    <Paperclip size={12} /> Attach
                                </button>
                            )}
                        </div>
                        {(section.evidence || []).length === 0 ? (
                            <p className="text-[12px] text-[#9A968F]">No supporting documents attached to this section yet.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {section.evidence.map((ev) => (
                                    <li key={ev.id} data-testid={`cpp-evidence-${ev.id}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: "#F0EDE8" }}>
                                        <button onClick={() => viewEvidence(ev.document_id)} className="inline-flex items-center gap-2 text-[12.5px] font-medium text-left min-w-0" style={{ color: "#2E6B57" }}>
                                            <FileText size={13} className="flex-shrink-0" /> <span className="truncate">{ev.title}</span>
                                        </button>
                                        {canEdit && (
                                            <button data-testid={`cpp-evidence-remove-${ev.id}`} onClick={() => removeEvidence(ev.id)} title="Unlink"
                                                className="w-6 h-6 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5] text-[#B33A2E] flex-shrink-0"><Unlink size={12} /></button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {evidenceOpen && (
                <DocumentLinkDialog open={evidenceOpen} clientId={clientId} title="Attach evidence"
                    onClose={() => setEvidenceOpen(false)} onUpload={uploadEvidence} onLink={linkEvidence} />
            )}
        </div>
    );
}
